import { useMemo, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import type { Task } from '@shared/types';
import { TaskRow } from '@/components/TaskRow';
import { CenteredSpinner, EmptyState, ErrorState } from '@/components/ui';
import { cn, dueStatus } from '@/lib/format';
import { useMe, useProjects, useTasks, useUpdateTask } from '@/lib/queries';

type Range = 'overdue' | 'today' | 'week' | 'all';

/**
 * The default landing view: what *I* have to do, grouped by urgency.
 *
 * The legacy app's equivalent ("All Open Tasks") was a flat, unsorted list of
 * every open task across every project — 3,000+ rows with no notion of whose
 * they were. This filters to the current user and buckets by due date.
 */
export function MyTasksPage() {
  const { data: me } = useMe();
  const { data: projects } = useProjects();
  const updateTask = useUpdateTask();
  const [range, setRange] = useState<Range>('week');
  const [showDone, setShowDone] = useState(false);

  const query = useTasks(
    {
      assigneeIds: me ? [me.id] : [],
      done: showDone ? undefined : false,
      sort: 'due_date',
      order: 'asc',
      perPage: 500,
    },
    // Don't fire until we know who "me" is, or we'd fetch everyone's tasks.
    { enabled: me !== undefined },
  );

  const projectNames = useMemo(
    () => new Map((projects?.items ?? []).map((p) => [p.id, p.title])),
    [projects],
  );

  const groups = useMemo(() => groupByUrgency(query.data?.items ?? [], range), [query.data, range]);

  if (query.isPending) return <CenteredSpinner label="Aufgaben werden geladen…" />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;

  const total = groups.reduce((sum, g) => sum + g.tasks.length, 0);

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b line surface-card px-5 py-3">
        <div className="mr-auto">
          <h1 className="text-base font-semibold text-strong">Meine Aufgaben</h1>
          <p className="text-xs text-muted">
            {total} {total === 1 ? 'Aufgabe' : 'Aufgaben'}
            {me ? ` · ${me.name}` : ''}
          </p>
        </div>

        <div className="flex rounded-lg surface-sunken p-0.5">
          {(
            [
              ['overdue', 'Überfällig'],
              ['today', 'Heute'],
              ['week', 'Diese Woche'],
              ['all', 'Alle'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              onClick={() => setRange(value)}
              className={cn(
                'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                range === value
                  ? 'surface-card text-strong shadow-sm'
                  : 'text-muted hover:text-strong',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted">
          <input
            type="checkbox"
            checked={showDone}
            onChange={(e) => setShowDone(e.target.checked)}
            className="size-3.5 rounded accent-brand-500"
          />
          Erledigte
        </label>
      </header>

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        {total === 0 ? (
          <EmptyState
            icon={<CheckCircle2 />}
            title="Nichts zu tun"
            description={
              range === 'all'
                ? 'Dir sind aktuell keine offenen Aufgaben zugewiesen.'
                : 'In diesem Zeitraum ist nichts fällig. Zeitraum wechseln, um mehr zu sehen.'
            }
          />
        ) : (
          groups.map((group) => (
            <section key={group.key}>
              <h2
                className="sticky top-0 z-10 flex items-center gap-2 border-b line
                           surface-raised/95 px-5 py-1.5 backdrop-blur"
              >
                <span className={cn('text-[11px] font-semibold uppercase tracking-wide', group.tone)}>
                  {group.title}
                </span>
                <span className="text-[11px] text-faint tabular-nums">{group.tasks.length}</span>
              </h2>
              <div className="divide-y line surface-card">
                {group.tasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    projectName={projectNames.get(task.projectId)}
                    onToggleDone={(t) =>
                      updateTask.mutate({ id: t.id, patch: { done: !t.done } })
                    }
                  />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}

interface Group {
  key: string;
  title: string;
  tone: string;
  tasks: Task[];
}

/**
 * Buckets tasks into urgency groups and drops the ones outside `range`.
 * Tasks with no due date land in their own group at the bottom rather than
 * being treated as "later" and hidden by a date filter.
 */
function groupByUrgency(tasks: Task[], range: Range): Group[] {
  const buckets: Record<string, Task[]> = {
    overdue: [], today: [], tomorrow: [], week: [], later: [], undated: [],
  };

  for (const task of tasks) {
    if (task.dueDate === null) {
      buckets.undated?.push(task);
      continue;
    }
    switch (dueStatus(task.dueDate, task.done)) {
      case 'overdue': buckets.overdue?.push(task); break;
      case 'today': buckets.today?.push(task); break;
      case 'tomorrow': buckets.tomorrow?.push(task); break;
      case 'soon': buckets.week?.push(task); break;
      // A completed task has status 'none' but still has a date; keep it with
      // the rest rather than mixing it into the undated group.
      default: buckets.later?.push(task);
    }
  }

  const all: Group[] = [
    { key: 'overdue', title: 'Überfällig', tone: 'text-red-600 dark:text-red-400', tasks: buckets.overdue ?? [] },
    { key: 'today', title: 'Heute', tone: 'text-orange-600 dark:text-orange-400', tasks: buckets.today ?? [] },
    { key: 'tomorrow', title: 'Morgen', tone: 'text-amber-600 dark:text-amber-500', tasks: buckets.tomorrow ?? [] },
    { key: 'week', title: 'Diese Woche', tone: 'text-body', tasks: buckets.week ?? [] },
    { key: 'later', title: 'Später', tone: 'text-muted', tasks: buckets.later ?? [] },
    { key: 'undated', title: 'Ohne Datum', tone: 'text-muted', tasks: buckets.undated ?? [] },
  ];

  const allowed: Record<Range, ReadonlySet<string>> = {
    overdue: new Set(['overdue']),
    today: new Set(['overdue', 'today']),
    week: new Set(['overdue', 'today', 'tomorrow', 'week']),
    all: new Set(all.map((g) => g.key)),
  };

  return all.filter((g) => allowed[range].has(g.key) && g.tasks.length > 0);
}
